import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useMemo, useRef, useState } from 'react'

import {
  type OrderDraftItemMutationStatusEnum,
  OrderDraftItemMutationStatuses,
} from './OrderDraftItemMutationStatusEnum'

export type OrderDraftItemMutationVisibleStatus = Exclude<
  OrderDraftItemMutationStatusEnum,
  typeof OrderDraftItemMutationStatuses.COMPLETED
>

type InProgressStatus =
  | typeof OrderDraftItemMutationStatuses.CREATING
  | typeof OrderDraftItemMutationStatuses.UPDATING
  | typeof OrderDraftItemMutationStatuses.DELETING

type FailedStatus =
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_CREATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_UPDATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_DELETE

type TerminalStatus =
  | typeof OrderDraftItemMutationStatuses.COMPLETED
  | FailedStatus

interface OrderDraftItemMutationEntry {
  status: OrderDraftItemMutationVisibleStatus
  latestMutationSeq: number
}

// No `| undefined` value: a settled item's key is deleted, never set to undefined, and
// `getItemsWithUnsettledMutations` reads key-presence alone.
type OrderDraftItemMutationStatusMap = Record<
  number,
  OrderDraftItemMutationEntry
>

// Overload signature for setItemMutationStatus to better type the function when
// called with InProgressStatus or TerminalStatus.
interface SetItemMutationStatus {
  (itemId: number, status: InProgressStatus): number
  (itemId: number, status: TerminalStatus, seq: number): void
}

interface IOrderDraftItemMutationStatusContextValue {
  getItemMutationStatus: (
    itemId: number
  ) => OrderDraftItemMutationVisibleStatus | null
  setItemMutationStatus: SetItemMutationStatus
  hasInFlightItemMutations: boolean
  /**
   * Returns the IDs of all items currently in a failed state
   * (FAILED_TO_CREATE, FAILED_TO_UPDATE, or FAILED_TO_DELETE).
   * Reads the latest committed state synchronously, so it's safe to call from
   * a closure without closure-staleness.
   */
  getItemsWithFailedMutations: () => number[]
  /**
   * IDs of items whose latest mutation has not completed successfully (in-flight or
   * failed). Safe to call from a closure.
   */
  getItemsWithUnsettledMutations: () => number[]
  /**
   * Ticks once per settled mutation round trip, so a background merge can tell whether its
   * snapshot predates a local write. Safe to call from a closure.
   */
  getSettledMutationCount: () => number
  // Use when the item leaves the cache outright; bypasses seq guarding
  // because there's no in-flight mutation to honor anymore.
  clearItemMutationStatus: (itemId: number) => void
}

export const OrderDraftItemMutationStatusContext =
  createContext<IOrderDraftItemMutationStatusContextValue | null>(null)

const isInProgressStatus = (
  status: OrderDraftItemMutationStatusEnum
): status is InProgressStatus =>
  status === OrderDraftItemMutationStatuses.CREATING ||
  status === OrderDraftItemMutationStatuses.UPDATING ||
  status === OrderDraftItemMutationStatuses.DELETING

const isFailedStatus = (
  status: OrderDraftItemMutationStatusEnum
): status is FailedStatus =>
  status === OrderDraftItemMutationStatuses.FAILED_TO_CREATE ||
  status === OrderDraftItemMutationStatuses.FAILED_TO_UPDATE ||
  status === OrderDraftItemMutationStatuses.FAILED_TO_DELETE

export const OrderDraftItemMutationStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [mutationStatuses, setMutationStatuses] =
    useState<OrderDraftItemMutationStatusMap>({})

  // Monotonically increasing counter for mutation ordering.
  // Lives in a ref so setItemMutationStatus can return the
  // assigned sequence synchronously.
  const seqCounterRef = useRef(0)

  // Counts settles rather than naming ids: a settled mutation's status entry is already gone.
  const settledMutationCountRef = useRef(0)

  const hasInFlightItemMutations = useMemo<boolean>(
    () =>
      Object.values(mutationStatuses).some(
        (entry) => entry !== undefined && isInProgressStatus(entry.status)
      ),
    [mutationStatuses]
  )

  // Mirror of mutationStatuses in a ref so getItemsWithFailedMutations can
  // stay stable across renders while still reading the latest committed
  // value — important as this may be called from a closure
  const mutationStatusesRef = useRef(mutationStatuses)
  mutationStatusesRef.current = mutationStatuses

  const getItemMutationStatus = useCallback(
    (itemId: number): OrderDraftItemMutationVisibleStatus | null => {
      return mutationStatuses[itemId]?.status ?? null
    },
    [mutationStatuses]
  )

  // Terminal writes carry the seq from the originating in-progress call, and
  // any write whose seq is older than the latest is dropped — so stale
  // completions/failures cannot clobber the state of a newer in-flight mutation.
  //
  // useMemo (rather than useCallback) because useCallback's inferred type does
  // not preserve function declaration overloads.
  const setItemMutationStatus = useMemo<SetItemMutationStatus>(() => {
    function impl(itemId: number, status: InProgressStatus): number
    function impl(itemId: number, status: TerminalStatus, seq: number): void
    function impl(
      itemId: number,
      status: OrderDraftItemMutationStatusEnum,
      seq?: number
    ): number | void {
      if (isInProgressStatus(status)) {
        const newSeq = ++seqCounterRef.current

        setMutationStatuses((prev) => ({
          ...prev,
          [itemId]: { status, latestMutationSeq: newSeq },
        }))

        return newSeq
      }

      if (typeof seq !== 'number') {
        throw new TypeError(
          `setItemMutationStatus: terminal status "${status}" requires a seq argument`
        )
      }

      // Outside the updater (React may call it twice), and counted even when the write below
      // is dropped as stale — a round trip still finished.
      settledMutationCountRef.current += 1

      setMutationStatuses((prev) => {
        const current = prev[itemId]

        // Drop stale write if...
        // 1. No active mutation
        if (!current) return prev

        // 2. The write's seq is not the latest
        if (seq < current.latestMutationSeq) return prev

        // Otherwise, update the stored status
        switch (status) {
          case OrderDraftItemMutationStatuses.COMPLETED: {
            const { [itemId]: _, ...rest } = prev
            return rest
          }
          case OrderDraftItemMutationStatuses.FAILED_TO_CREATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_UPDATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_DELETE:
            return {
              ...prev,
              [itemId]: { status, latestMutationSeq: seq },
            }
          default: {
            const _exhaustiveCheck: never = status
            return _exhaustiveCheck
          }
        }
      })
    }

    return impl
  }, [])

  const getItemsWithFailedMutations = useCallback((): number[] => {
    return Object.entries(mutationStatusesRef.current)
      .filter(
        ([, entry]) => entry !== undefined && isFailedStatus(entry.status)
      )
      .map(([id]) => Number(id))
  }, [])

  // No status predicate needed: a COMPLETED write deletes the entry, so any id that
  // still has one is unsettled.
  const getItemsWithUnsettledMutations = useCallback((): number[] => {
    return Object.keys(mutationStatusesRef.current).map(Number)
  }, [])

  // Not ticked by clearItemMutationStatus: client-only items never reach the server.
  const getSettledMutationCount = useCallback(
    (): number => settledMutationCountRef.current,
    []
  )

  const clearItemMutationStatus = useCallback((itemId: number): void => {
    setMutationStatuses((prev) => {
      if (!prev[itemId]) return prev
      const { [itemId]: _, ...rest } = prev
      return rest
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      getItemMutationStatus,
      setItemMutationStatus,
      hasInFlightItemMutations,
      getItemsWithFailedMutations,
      getItemsWithUnsettledMutations,
      getSettledMutationCount,
      clearItemMutationStatus,
    }),
    [
      getItemMutationStatus,
      setItemMutationStatus,
      hasInFlightItemMutations,
      getItemsWithFailedMutations,
      getItemsWithUnsettledMutations,
      getSettledMutationCount,
      clearItemMutationStatus,
    ]
  )

  return (
    <OrderDraftItemMutationStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftItemMutationStatusContext.Provider>
  )
}
